// Shared utility for determining listing status and actions
// Used by both mylistings.js and sellerPage.js

/**
 * Determines the listing status and action based on transactions, invites, and purchase requests
 * @param {Object} item - The listing item with transactions, invites, and purchase_requests
 * @returns {Object} Status object with statusText, statusColor, action, actionText, etc.
 */
export const getListingStatus = (item) => {
  // Check if item is cancelled - highest priority
  if (item.status === "cancelled") {
    return {
      status: "cancelled",
      statusText: "Cancelled",
      statusColor: "bg-red-500 text-white",
      description: "This item has been cancelled",
      action: "no_action",
      actionText: "NO ACTION",
    };
  }

  const transactions = Array.isArray(item.transactions)
    ? item.transactions
    : [];
  const invites = Array.isArray(item.invites) ? item.invites : [];
  const purchaseRequests = Array.isArray(item.purchase_requests)
    ? item.purchase_requests
    : Array.isArray(item.offers)
    ? item.offers
    : [];

  // Get the most recent/active transaction
  const activeTransaction = transactions.length > 0 ? transactions[0] : null;
  const txStatus = activeTransaction?.status?.toLowerCase();
  const adminVerificationStatus =
    activeTransaction?.admin_verification_status?.toLowerCase();
  const sellerConfirmed = activeTransaction?.seller_confirmed || false;
  const sellerFundsConfirmed =
    activeTransaction?.seller_funds_confirmed || false;
  const buyerConfirmed = activeTransaction?.buyer_confirmed || false;

  // Check if transaction is cancelled
  if (txStatus === "cancelled") {
    return {
      status: "cancelled",
      statusText: "Cancelled",
      statusColor: "bg-red-500 text-white",
      description:
        activeTransaction?.payment_cancelled_reason ===
        "item_not_authentication"
          ? "Cancelled - Authentication failed"
          : "This item has been cancelled",
      action: "no_action",
      actionText: "NO ACTION",
      transactionId: activeTransaction?.id,
    };
  }

  // Consider only active offers/invites (ignore cancelled/rejected/expired)
  // Treat only PENDING requests as active; accepted requests can stay but if the invite got cancelled
  // we don't want to keep the card in "Offers Received".
  const hasOffersReceived = (purchaseRequests || []).some(
    (pr) => pr?.status?.toLowerCase() === "pending"
  );

  const hasOffersSent = (invites || []).some((inv) =>
    ["pending", "listed"].includes(inv?.status?.toLowerCase())
  );

  // Check if there's an accepted invite (payment pending)
  const hasAcceptedInvite = (invites || []).some(
    (inv) => inv?.status?.toLowerCase() === "accepted"
  );

  // Check if there's an accepted purchase request (offer accepted by seller)
  const hasAcceptedPurchaseRequest = (purchaseRequests || []).some(
    (pr) => pr?.status?.toLowerCase() === "accepted"
  );

  // If there's an accepted invite or accepted purchase request but no transaction yet, show "Payment Pending"
  if (
    (hasAcceptedInvite || hasAcceptedPurchaseRequest) &&
    (!activeTransaction || !txStatus)
  ) {
    return {
      status: "pending_payment",
      statusText: "pending payment",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: hasAcceptedPurchaseRequest
        ? "Awaiting escrow payment"
        : "Buyer accepted invite - awaiting payment",
      action: "no_action",
      actionText: "NO ACTION",
    };
  }

  // If no active transaction AND no active offers/invites, revert to listed
  // (treat rejected/cancelled/expired as closed)
  if (
    (!activeTransaction || !txStatus) &&
    !hasOffersReceived &&
    !hasOffersSent &&
    !hasAcceptedInvite &&
    !hasAcceptedPurchaseRequest
  ) {
    return {
      status: "listed",
      statusText: "listed",
      statusColor: "bg-[#E0E0E0] text-[#575757]",
      description: "",
      action: "send_offers",
      actionText: "Send new offers",
      actionIcon: "envelope",
      actionColor: "bg-blue-50",
    };
  }

  // Priority order: Check transaction statuses first (most important)

  // 9. funds released - Transaction released, seller needs to confirm funds
  if (txStatus === "released" && !sellerFundsConfirmed) {
    return {
      status: "funds_released",
      statusText: "funds released",
      statusColor: "bg-[#CBECF9] text-[#003366]",
      description: "You need to confirm the funds reception",
      action: "confirm_funds",
      actionText: "Confirm funds reception",
      actionIcon: "checkmark",
      actionColor: "bg-green-500",
      transactionId: activeTransaction?.id,
    };
  }

  // 10. completed - funds released and seller confirmed reception
  if (txStatus === "released" && sellerFundsConfirmed) {
    return {
      status: "completed",
      statusText: "completed",
      statusColor: "bg-[#B9FFCC] text-[#6C8F75]",
      description: "",
      action: "completed",
      actionText: "See receipt",
      actionIcon: "checkmark",
      transactionId: activeTransaction?.id,
    };
  }

  // 8a. Seller can confirm first (accepted/verified or confirmed states)
  if (
    ["accepted", "confirmed", "buyer_confirmed"].includes(txStatus) &&
    !sellerConfirmed
  ) {
    return {
      status: "funds_on_hold_seller",
      statusText: "funds on hold",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: "Awaiting transaction confirmation",
      action: "confirm_transaction",
      actionText: "Confirm transaction done",
      actionIcon: "checkmark",
      actionColor: "bg-green-500",
      transactionId: activeTransaction?.id,
    };
  }

  // 8b. Seller already confirmed, waiting for buyer
  if (sellerConfirmed && !buyerConfirmed) {
    return {
      status: "funds_on_hold_buyer",
      statusText: "funds on hold",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: "Awaiting buyer confirmation",
      action: "no_action",
      actionText: "NO ACTION",
      transactionId: activeTransaction?.id,
    };
  }

  if (
    (txStatus === "accepted" &&
      adminVerificationStatus === "verified" &&
      !buyerConfirmed) ||
    (txStatus === "confirmed" && !buyerConfirmed)
  ) {
    return {
      status: "funds_on_hold_buyer",
      statusText: "funds on hold",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: "Awaiting transaction confirmation",
      action: "no_action",
      actionText: "NO ACTION",
      transactionId: activeTransaction?.id,
    };
  }

  // 7. funds on hold (awaiting seller confirmation)
  if (
    (txStatus === "buyer_confirmed" ||
      (txStatus === "confirmed" && buyerConfirmed)) &&
    !sellerConfirmed
  ) {
    return {
      status: "funds_on_hold_seller",
      statusText: "funds on hold",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: "Awaiting transaction confirmation",
      action: "confirm_transaction",
      actionText: "Confirm transaction done",
      actionIcon: "checkmark",
      actionColor: "bg-green-500",
      transactionId: activeTransaction?.id,
    };
  }

  // 6.5. Both confirmed - awaiting escrow transfer
  // After both buyer and seller confirm, show "Confirmed, Awaiting Escrow Transfer"
  if (txStatus === "confirmed" && buyerConfirmed && sellerConfirmed) {
    return {
      status: "confirmed_awaiting_transfer",
      statusText: "funds on hold",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: "An admin is checking the payment",
      action: "no_action",
      actionText: "NO ACTION",
      transactionId: activeTransaction?.id,
    };
  }

  // 6. Verification in progress
  // Check if payment was initiated (buyer clicked "I made the transfer")
  // Only show this if admin hasn't verified yet
  const paymentInitiated = activeTransaction?.payment_initiated_at;

  if (
    paymentInitiated ||
    (txStatus === "accepted" && adminVerificationStatus !== "verified") ||
    txStatus === "paid" ||
    adminVerificationStatus === "verifying"
  ) {
    return {
      status: "verification_in_progress",
      statusText: "Verification in progress",
      statusColor: "bg-[#E0E0E0] text-[#575757]",
      description: "An admin is checking the payment",
      action: "no_action",
      actionText: "NO ACTION",
      transactionId: activeTransaction?.id,
    };
  }

  // 5. pending payment (only if payment not yet initiated)
  if (txStatus === "pending") {
    return {
      status: "pending_payment",
      statusText: "pending payment",
      statusColor: "bg-[#FFC49F] text-[#D35100]",
      description: "Awaiting escrow payment",
      action: "no_action",
      actionText: "NO ACTION",
      transactionId: activeTransaction?.id,
    };
  }

  // 2. Offers Received (when buyer sends an offer)
  if (hasOffersReceived) {
    return {
      status: "offers_received",
      statusText: "Offers Received",
      statusColor: "bg-[#8794E0] text-[#FFFFFF]",
      description: "",
      action: "see_offers",
      actionText: "See offers",
      actionIcon: "search",
      actionColor: "bg-blue-50",
    };
  }

  // 3. Offers Sent (when seller sends invitations to buyer)
  if (hasOffersSent) {
    return {
      status: "offers_sent",
      statusText: "Offers Sent",
      statusColor: "bg-[#5C31AC] text-[#FFFFFF]",
      description: "Awaiting responses",
      action: "send_offers",
      actionText: "Send new offers",
      actionIcon: "envelope",
      actionColor: "bg-blue-50",
    };
  }

  // 1. listed - Default status (no transactions, no offers)
  return {
    status: "listed",
    statusText: "listed",
    statusColor: "bg-[#E0E0E0] text-[#575757]",
    description: "",
    action: "send_offers",
    actionText: "Send new offers",
    actionIcon: "envelope",
    actionColor: "bg-blue-50",
  };
};
